import { Flex, Tabs } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import GlobalMapFilterBar from "./filters/GlobalMapFilterBar.tsx"
import { HistoryToggleButton } from "./filters/HistoryToggleButton.tsx"
import { LayerToggleButtons } from "./filters/LayerToggleButtons.tsx"
import { selectActiveTab, setActiveTab } from "./redux/global-map.slice.ts"

/**
 * Two explicit rows, not one wrapping flex row sharing everything — an
 * earlier version put the tab list, every picker, "Projects & Leads", and
 * Clear All/View All all in one `Flex wrap` container, which read fine at
 * full desktop width but wrapped unpredictably (a picker stranded next to
 * the tabs, the toggle pair splitting from Clear All) on a laptop-width
 * screen, per an explicit product report. Row 1 is the Map/List/Analytics
 * tab list beside `LayerToggleButtons` (moved out of `GlobalMapFilterBar`,
 * which no longer renders it) — these two belong together as "what the map
 * itself is showing". Row 2 is `GlobalMapFilterBar` in full — every picker
 * plus Clear All/View All, its own internal `justify="space-between"`
 * unchanged — "what's being filtered out". (Row order was swapped from the
 * first pass per explicit product direction; the two rows' own contents are
 * otherwise unchanged.) Each row still wraps internally at its own
 * breakpoint (Step 8's responsive requirement) — this only fixes the
 * CROSS-row wrapping that was mixing the two concerns.
 */
interface GlobalMapToolbarProps {
  onViewAll?: () => void
  onClearSearch?: () => void
}

const GlobalMapToolbar = ({
  onViewAll,
  onClearSearch
}: GlobalMapToolbarProps) => {
  const { t } = useTranslation()
  const activeTab = useAppSelector(selectActiveTab)
  const dispatch = useAppDispatch()

  const items = [
    { key: "map", label: t("globalMap.tabs.map") },
    { key: "list", label: t("globalMap.tabs.list") },
    { key: "analytics", label: t("globalMap.tabs.analytics") }
  ]

  return (
    <Flex vertical gap={12} className="shrink-0">
      <Flex wrap align="center" gap={16}>
        <Tabs
          activeKey={activeTab}
          items={items}
          onChange={(key) =>
            dispatch(setActiveTab(key as "map" | "list" | "analytics"))
          }
          className="shrink-0 [&_.ant-tabs-nav]:mb-0"
        />
        {/* The Lead/Project/History trio renders on every tab, including
            List (reversed per explicit product direction — the List's own
            entity sub-tabs (All/Leads/Projects/Subcontractors/Appointments)
            narrow independently, but `activeLayers` already feeds
            `useListFeatures`'s own `entities` param — see that hook — so
            toggling Lead/Project here is a real, additional narrowing on
            the List tab too, not just a Map-tab cosmetic). `HistoryToggleButton`
            has always been tab-agnostic — see its own docstring — since
            History affects Map, List, and Analytics alike. This inner Flex
            (gap 8) keeps History visually grouped closer to Lead/Project
            than the outer row's gap (16, still separating the whole trio
            from the tab list) — a deliberate, smaller "related controls"
            gap per explicit product direction, not tight/touching. */}
        <Flex align="center" gap={8}>
          <LayerToggleButtons />
          <HistoryToggleButton />
        </Flex>
      </Flex>
      <GlobalMapFilterBar onViewAll={onViewAll} onClearSearch={onClearSearch} />
    </Flex>
  )
}

export default GlobalMapToolbar
